import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import * as d3 from 'd3';
import { BrowserModule } from '@angular/platform-browser';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';
import { parseToNativeDate } from '../../../utils/date-utils';

export interface ItooltipExtra {
  key: string;
  label: string;
  isAmount: boolean;
}
@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
  providers: [CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class LineChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() showAllXLabels: boolean = false;
  @Input() showXAxisGrid: boolean = false;
  @Input() xAxisShowOnlyFirstLast: boolean = false;
  @Input() showAllXticks: boolean = false;
  @Input() data1: any[] = [];
  @Input() data2: any[] = [];
  @Input() data3: any[] = [];
  @Input() data4: any[] = [];
  @Input() data5: any[] = [];
  @Input() data6: any[] = [];
  @Input() data7: any[] = [];

  @Input() valueKey: string = '';
  @Input() unit: string = 'week';
  @Input() isAmount: boolean = false;
  @Input() valueFormat: 'currency' | 'number' | 'percent' = 'number'; // Format type for tooltip values
  @Input() tooltipExtraData: ItooltipExtra[] = [];

  // --- Styling Inputs (deprecated - now managed by theme) ---
  @Input() primaryLineColor: string = '';
  @Input() secondaryLineColor: string = '';
  @Input() tertiaryLineColor: string = '';
  @Input() quaternaryLineColor: string = '';
  @Input() quinaryLineColor: string = '';
  @Input() senaryLineColor: string = '';
  @Input() septenaryLineColor: string = '';
  @Input() octonaryLineColor: string = '';
  @Input() nonaryLineColor: string = '';
  @Input() denaryLineColor: string = '';
  @Input() axisColor: string = '';
  @Input() axisTickColor: string = '';
  @Input() axisLabelColor: string = '';
  @Input() minHeight: number = 250;
  @Input() marginTop: number = 30;
  @Input() marginBottom: number = 40;

  // --- Feature Flag Inputs ---
  @Input() showXAxis: boolean = true;
  @Input() showYAxis: boolean = true;
  @Input() yAxisShowOnlyFirstLast: boolean = false;
  @Input() showYAxisGrid: boolean = true;
  @Input() showCircleStrokes: boolean = false;
  @Input() showCircleStrokesWithStroke: boolean = false;
  @Input() strokeWithLineColor: boolean = false;

  // Custom margins { top, right, bottom, left }
  @Input() marginProps: any = {};
  @Input() setBg: boolean = false;
  @Input() showAllYticks: boolean = false;
  @Input() showYAxisPath: boolean = true;
  isReverse: Boolean = false;
  @Input() curveType:
    | 'curveLinear'
    | 'curveMonotoneX'
    | 'curveStep'
    | 'curveStepBefore'
    | 'curveStepAfter' = 'curveLinear';
  @Input() dashedSeries: number[] = []; // Array of series indices (0-based) to be dashed


  @ViewChild('chartContainer', { static: true })
  private chartContainer!: ElementRef;
  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';

  private themeService = inject(ThemeService);
  private lastWidth = 0;
  private lastHeight = 0;

  constructor(private currencyPipe: CurrencyPipe) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['data1'] ||
        changes['data2'] ||
        changes['data3'] ||
        changes['data4'] ||
        changes['data5'] ||
        changes['data6'] ||
        changes['data7']) &&
      this.chartContainer?.nativeElement
    ) {
      if (changes['data2'] && changes['data2'].currentValue.length == 0) {
        this.isReverse = false;
      }

      this.createChart();
    }
  }

  ngAfterViewInit(): void {
    // Subscribe to theme changes
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.createChart(); // Redraw chart with new theme colors
    });

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Avoid infinite growth by checking for significant change
        if (Math.abs(width - this.lastWidth) > 3 || Math.abs(height - this.lastHeight) > 3) {
          this.lastWidth = width;
          this.lastHeight = height;
          this.createChart();
        }
      }
    });

    if (this.chartContainer?.nativeElement) {
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  // Get theme-aware colors
  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      primaryLine: this.primaryLineColor || (isDark ? '#AD46FF' : '#8b5cf6'), // purple-400 / purple-600
      secondaryLine:
        this.secondaryLineColor || (isDark ? '#A1A1AA' : '#7D7D93'), // slate-500 / gray-400
      tertiaryLine: this.tertiaryLineColor || '#4EA1FF', // slate-500 / gray-400
      quaternaryLine: this.quaternaryLineColor || '#FF7AA2', // slate-500 / gray-400
      quinaryLine: this.quinaryLineColor || '#14D18E', // slate-500 / gray-400
      senaryLine: this.senaryLineColor || '#FFD76B', // slate-500 / gray-400
      septenaryLine: this.septenaryLineColor || '#8B5CF6', // slate-500 / gray-400
      octonaryLine: this.octonaryLineColor || '#F59E0B', // slate-500 / gray-400
      nonaryLine: this.nonaryLineColor || '#F05050', // slate-500 / gray-400
      denaryLine: this.denaryLineColor || '#15B268', // slate-500 / gray-400
      axis: this.axisColor || (isDark ? '#47474e' : '#DFE5ED'), // slate-600 / gray-300
      axisTick: this.axisTickColor || (isDark ? '#A1A1AA' : '#7D7D93'), // slate-300 / dark-gray
      axisLabel: this.axisLabelColor || (isDark ? '#d4d4d8' : '#131921'), // slate-300 / dark-gray
      tooltipBg: isDark ? '#1e1e21' : '#ffffff', // slate-800 / white
      tooltipBorder: isDark ? '#47474e' : '#e5e7eb', // slate-600 / gray-200
      tooltipText: isDark ? '#f4f4f5' : '#000000', // slate-100 / black
      tooltipSecondaryText: isDark ? '#a1a1aa' : '#6b7280', // slate-400 / gray-500
      gridLine: isDark ? '#47474e' : '#DFE5ED', // same as axis color
      hoverLine: isDark ? '#85858e' : '#A2A8B0', // slate-500 / gray-400
      badgePositiveBg: isDark ? '#064e3b' : '#d1fae5', // emerald-900 / emerald-100
      badgePositiveBorder: isDark ? '#10b981' : '#34d399', // emerald-500 / emerald-400
      badgePositiveText: isDark ? '#6ee7b7' : '#047857', // emerald-300 / emerald-700
      badgeNegativeBg: isDark ? '#7c2d12' : '#fed7aa', // orange-900 / orange-100
      badgeNegativeBorder: isDark ? '#f97316' : '#fb923c', // orange-500 / orange-400
      badgeNegativeText: isDark ? '#fdba74' : '#c2410c', // orange-300 / orange-700
      divider: isDark ? '#47474e' : '#cbd5e1', // slate-600 / slate-300
      backgroundGradient: isDark
        ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.8) 35%, rgba(30, 41, 59, 0.6) 96%)' // slate-900/800
        : 'linear-gradient(180deg,rgba(242, 245, 248, 1) 35%, rgba(255, 255, 255, 1) 96%)',
      xAxisOverlayBg: isDark
        ? 'rgba(30, 41, 59, 0.5)'
        : 'rgba(242, 245, 248, 0.5)', // slate-800 / light gray
    };
  }

  private timezoneDate(dateStr: string | undefined): Date | undefined {
    return parseToNativeDate(dateStr, this.unit);
  }

  private unitFormatter(): (start: Date, end?: Date) => string {
    const currentYear = new Date().getUTCFullYear();

    return (start: Date, end?: Date): string => {
      const year = start.getUTCFullYear();
      const endYear = end?.getUTCFullYear() ?? year;
      const isSameYearAsCurrent = year === endYear && year === currentYear;

      switch (this.unit) {
        case 'year':
          return d3.utcFormat('%Y')(start);
        case 'quarter':
          if (!end) {
            const q = Math.ceil((start.getUTCMonth() + 1) / 3);
            return isNaN(q) ? 'N/A' : `Q${q} ${start.getUTCFullYear()}`;
          }
          const startQ = Math.ceil((start.getUTCMonth() + 1) / 3);
          const endQ = Math.ceil((end.getUTCMonth() + 1) / 3);
          if (year !== endYear) {
            return `Q${startQ} ${year} - Q${endQ} ${endYear}`;
          }
          if (startQ !== endQ) {
            return `Q${startQ} - Q${endQ}, ${year}`;
          }
          return `Q${startQ} ${year}`;
        case 'month':
          return d3.utcFormat('%b %Y')(start);
        case 'week':
          if (!end) {
            return d3.utcFormat('%d %b %Y')(start);
          }
          if (year !== endYear) {
            return `${d3.utcFormat('%b %d, %Y')(start)} - ${d3.utcFormat(
              '%b %d, %Y'
            )(end)}`;
          }
          if (start.getUTCMonth() !== end.getUTCMonth()) {
            return `${d3.utcFormat('%b %d')(start)} - ${d3.utcFormat(
              '%b %d, %Y'
            )(end)}`;
          }
          return `${d3.utcFormat('%b %d')(start)} - ${d3.utcFormat('%d, %Y')(
            end
          )}`;
        case 'day':
          return d3.utcFormat(isSameYearAsCurrent ? '%d %b' : '%d %b %Y')(
            start
          );
        case 'hour':
          return d3.utcFormat('%d %b %H:%M')(start);
        default:
          return d3.utcFormat('%Y-%m-%d')(start);
      }
    };
  }

  private getWidestLabelWidth(labels: string[], font = '12px Inter'): number {
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return 40;
    context.font = font;
    return Math.max(...labels.map((label) => context.measureText(label).width));
  }

  private calculatePercentChange(current: any, previous: any): number {
    const currentVal = parseFloat(current);
    const previousVal = parseFloat(previous);
    if (isNaN(currentVal) || isNaN(previousVal) || previousVal === 0) return 0;
    return ((currentVal - previousVal) / Math.abs(previousVal)) * 100;
  }

  private customToolTipHtml(
    hasPreviousData: boolean,
    percentChangeNum: number,
    currentUnitPeriod: string,
    previousUnitPeriod: string,
    currentValue: number,
    previousValue: number,
    isAmount: boolean,
    extraData: any[] = []
  ): string {
    const colors = this.getThemeColors();

    const formatValue = (val: number) => {
      if (val == null || isNaN(val)) return 'N/A';

      switch (this.valueFormat) {
        case 'currency':
          // Format with $ prefix and comma separators, up to 2 decimals
          const formatted = val.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
          return `$${formatted}`;
        case 'percent':
          return `${val.toFixed(1)}%`;
        default:
          return val.toLocaleString('en-US');
      }
    };

    let percentChangeHtml = '';
    if (hasPreviousData) {
      const isPositive = percentChangeNum > 0;
      const badgeClass = isPositive
        ? `bg-[${colors.badgePositiveBg}] border-[${colors.badgePositiveBorder}] text-[${colors.badgePositiveText}]`
        : `bg-[${colors.badgeNegativeBg}] border-[${colors.badgeNegativeBorder}] text-[${colors.badgeNegativeText}]`;
      const badgeText = `${isPositive ? '+' : ''}${percentChangeNum.toFixed(
        1
      )}%`;

      percentChangeHtml = `
        <div class="flex justify-end">
          <div class="border py-0.5 px-2 rounded-md font-semibold text-xs" style="
            background-color: ${
              isPositive ? colors.badgePositiveBg : colors.badgeNegativeBg
            };
            border-color: ${
              isPositive
                ? colors.badgePositiveBorder
                : colors.badgeNegativeBorder
            };
            color: ${
              isPositive ? colors.badgePositiveText : colors.badgeNegativeText
            };
            padding: 0.15rem 0.25rem !important;
          ">${badgeText}</div>
        </div>`;
    }

    let previousPeriodHtml = '';
    if (hasPreviousData) {
      previousPeriodHtml = `
        <div class="flex justify-between items-center gap-5 ${
          this.isReverse ? 'order-1' : 'order-2'
        }">
          <div class="flex justify-start items-center gap-2">
            <div style="background-color: ${
              !this.isReverse ? colors.secondaryLine : colors.primaryLine
            }" class="w-2 h-2 rounded-full"></div>
            <div class="font-medium text-xs" style="color: ${
              colors.tooltipSecondaryText
            }">${previousUnitPeriod}</div>
          </div>
          <div class="font-semibold text-xs" style="color: ${
            colors.tooltipText
          }">${formatValue(previousValue)}</div>
        </div>`;
    }

    let extraValuesHtml = '';
    if (!hasPreviousData && extraData && extraData.length > 0) {
      const extraRows = extraData
        .map(({ label, value, isAmount: itemIsAmount = false }) => {
          // For extra data items, respect individual isAmount flags
          const displayValue = itemIsAmount
            ? `$${value.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}`
            : value;

          return `
        <div class="flex justify-between items-center gap-5">
            <div class="font-semibold" style="font-size: 12px; color: ${colors.tooltipSecondaryText}">${label}</div>
            <div class="font-semibold" style="font-size: 12px; color: ${colors.tooltipText}">${displayValue}</div>
        </div>`;
        })
        .join('');

      extraValuesHtml = hasPreviousData
        ? `<div class="h-[1px] my-1" style="background-color: ${colors.divider}"></div>${extraRows}`
        : '';
    }

    const finalHtml = `
    <div class="shadow-lg px-2 py-1 flex flex-col gap-1.5 rounded-md border" style="
      background-color: ${colors.tooltipBg};
      border-color: ${colors.tooltipBorder};
      padding: 0.5rem !important;
    ">
    ${
      percentChangeHtml
        ? `<div class="flex flex-col justify-between gap-0.5">
          ${percentChangeHtml}
          ${
            percentChangeHtml
              ? `<div class="h-px my-1" style="background-color: ${colors.divider}"></div>`
              : ''
          }
        </div>`
        : ''
    }
      <div class="flex justify-between items-center gap-5 ${
        this.isReverse ? 'order-2' : 'order-1'
      }">
        <div class="flex justify-start items-center gap-2">
          <div style="background-color: ${
            this.isReverse ? colors.secondaryLine : colors.primaryLine
          }" class="w-2 h-2 rounded-full"></div>
          <div class="font-medium text-xs" style="color: ${
            colors.tooltipSecondaryText
          }">${currentUnitPeriod}</div>
        </div>
        <div class="font-semibold text-xs" style="color: ${
          colors.tooltipText
        }">${formatValue(currentValue)}</div>
      </div>
      ${previousPeriodHtml}
      ${extraValuesHtml}
    </div>`;
    return finalHtml;
  }

  private yearIndexModify(
    currentData: any[],
    previousData: any[]
  ): { currentData: any[]; previousData: any[] } {
    if (!previousData.length || currentData.length === previousData.length) {
      return { currentData, previousData };
    }

    const isCurrentShorter = currentData.length < previousData.length;
    const shorterData = isCurrentShorter ? [...currentData] : [...previousData];
    const longerData = isCurrentShorter ? previousData : currentData;

    if (longerData.length - shorterData.length !== 1) {
      return { currentData, previousData };
    }

    const insertionIndex = 59;

    if (shorterData.length < insertionIndex) {
      return { currentData, previousData };
    }

    const sampleObject = shorterData[insertionIndex - 1];
    const dummyLeapDay = { ...sampleObject };

    for (const key in dummyLeapDay) {
      if (Object.prototype.hasOwnProperty.call(dummyLeapDay, key)) {
        if (
          key.toLowerCase().includes('date') ||
          key.toLowerCase().includes('label')
        ) {
          dummyLeapDay[key] = '-';
        } else if (key.toLowerCase().includes('value')) {
          dummyLeapDay[key] = 'no data';
        } else if (typeof dummyLeapDay[key] === 'number') {
          dummyLeapDay[key] = 0;
        } else {
          dummyLeapDay[key] = null;
        }
      }
    }

    shorterData.splice(insertionIndex, 0, dummyLeapDay);

    if (isCurrentShorter) {
      return { currentData: shorterData, previousData };
    } else {
      return { currentData, previousData: shorterData };
    }
  }

  //   private createChart(): void {
  //     const container = this.chartContainer.nativeElement;
  //     if (!container || !this.data1 || this.data1.length === 0) {
  //       d3.select(container).selectAll('*').remove();
  //       while (container.firstChild) container.removeChild(container.firstChild);
  //       return;
  //     }

  //     d3.select(container).selectAll('*').remove();
  //     while (container.firstChild) container.removeChild(container.firstChild);

  //     // Get theme colors
  //     const colors = this.getThemeColors();

  //     const dataArr = [this.data1, this.data2, this.data3, this.data4].filter((d) => d && d.length > 0);

  //     const formatter = this.unitFormatter();

  //     const modifiedData = dataArr.map((dataset) =>
  //       dataset
  //         .map((ds) => {
  //           const tooltipExtra = this.tooltipExtraData.length
  //             ? this.tooltipExtraData
  //               .filter((extra) =>
  //                 Object.prototype.hasOwnProperty.call(ds, extra.key)
  //               )
  //               .map((extra) => ({
  //                 label: extra.label,
  //                 value: ds[extra.key],
  //                 isAmount: extra.isAmount,
  //               }))
  //             : [];

  //           return {
  //             start: this.timezoneDate(ds?.start),
  //             end: this.timezoneDate(ds?.end),
  //             value: ds[this.valueKey] || 0,
  //             // label: ds.label,
  //             label: formatter(
  //               this.timezoneDate(ds.start),
  //               this.timezoneDate(ds.end)
  //             ),
  //             tooltipExtraData: tooltipExtra,
  //             name: ds.key || '',
  //           };
  //         })
  //         .sort((a, b) => a.start.getTime() - b.start.getTime())
  //     );

  //     let currentData: any[] = [];
  //     let previousData: any[] = [];
  //     if (modifiedData.length >= 2) {
  //       const [firstSet, secondSet] = modifiedData;
  //       if (/* true || */ firstSet.length > secondSet.length) {
  //         currentData =
  //           firstSet[0].start > secondSet[0].start ? firstSet : secondSet;
  //         previousData =
  //           firstSet[0].start > secondSet[0].start ? secondSet : firstSet;
  //       } else {
  //         currentData = secondSet;
  //         // secondSet[0].start > secondSet[0].start ? firstSet : secondSet;
  //         previousData = firstSet;

  //         this.isReverse = true;
  //         // secondSet[0].start > secondSet[0].start ? secondSet : firstSet;
  //       }
  //     } else if (modifiedData.length === 1) {
  //       currentData = modifiedData[0];
  //     }

  //     if (currentData.length === 0) return;

  //     const alignedData = this.yearIndexModify(currentData, previousData);
  //     currentData = alignedData.currentData;
  //     previousData = alignedData.previousData;

  //     const placeholderIndex = currentData.findIndex(
  //       (d) => typeof d.value !== 'number'
  //     );
  //     if (placeholderIndex > -1) {
  //       const feb28Date = currentData[placeholderIndex - 1]?.start;
  //       if (feb28Date) {
  //         const feb29Date = new Date(feb28Date.getTime() + 24 * 60 * 60 * 1000);
  //         currentData[placeholderIndex].start = feb29Date;
  //       }
  //     }

  //     const minDate = d3.min(currentData, (d) => d.start)!;
  //     const maxDate = d3.max(currentData, (d) => d.start)!;
  //     const combinedValues = [...currentData, ...previousData];
  //     const numericValues = combinedValues
  //       .map((d) => d.value)
  //       .filter((v) => typeof v === 'number');
  //     const maxValue = d3.max(numericValues) ?? 0;
  //     const yTempScale = d3.scaleLinear().domain([0, maxValue]).nice();
  //     const yLabels = yTempScale.ticks().map((d) => d.toString());
  //     const widestLabelWidth = this.getWidestLabelWidth(yLabels);

  //     const width = container.clientWidth;
  //     const height = container.clientHeight;
  //     const marginLeft =
  //       this.marginProps.left ?? (this.showYAxis ? widestLabelWidth + 12 : 10);
  //     const marginRight = this.marginProps.right ?? 20;
  //     const marginTop = this.marginProps.top ?? 20;
  //     const marginBottom = this.marginProps.bottom ?? (this.showXAxis ? 30 : 10);

  //     const x = d3
  //       .scaleUtc()
  //       .domain([minDate, maxDate])
  //       .range([marginLeft, width - marginRight]);
  //     const y = d3
  //       .scaleLinear()
  //       .domain([0, maxValue])
  //       .range([height - marginBottom, marginTop])
  //       .nice();

  //     const svg = d3
  //       .select(container)
  //       .append('svg')
  //       .attr('width', width)
  //       .attr('height', height + 10);

  //     if (this.setBg) {
  //       svg.style('background', colors.backgroundGradient);
  //     }

  //     if (true) {
  //       svg
  //         .append('g')
  //         .attr('transform', `translate(${marginLeft},0)`)
  //         .call(
  //           d3
  //             .axisLeft(y)
  //             .tickSize(0)
  //             // .tickFormat('')
  //             .tickValues(d3.scaleLinear().domain(y.domain()).ticks(6))
  //         )
  //         .call((g) =>
  //           g
  //             .selectAll('.tick line')
  //             .clone()
  //             .attr('stroke', colors.gridLine)
  //             .attr('stroke-opacity', 0.4)
  //             .attr('x2', width - marginLeft - marginRight)
  //         )
  //         .call((g) => g.select('.domain').remove())
  //         .call((g) => g.selectAll('.tick text').remove());
  //     }

  //     if (this.showXAxis) {
  //       // const firstDate = currentData[0]?.start;
  //       // const lastDate = currentData[currentData.length - 1]?.start;
  //       // const xAxis = d3
  //       //   .axisBottom(x)
  //       //   .tickValues([firstDate, lastDate].filter((d) => d instanceof Date))
  //       //   .tickFormat((d: any) => {
  //       //     const dataEntry = currentData.find(
  //       //       (cd) => cd.start.getTime() === d.getTime()
  //       //     );
  //       //     return dataEntry ? dataEntry.label : '';
  //       //   });

  //       let tickValues: any[];

  //       if (this.showAllXLabels) {
  //         // SHOW ALL LABELS
  //         tickValues = currentData.map(d => d.start);
  //       } else {
  //         // SHOW ONLY FIRST & LAST
  //         const firstDate = currentData[0]?.start;
  //         const lastDate = currentData[currentData.length - 1]?.start;
  //         tickValues = [firstDate, lastDate].filter((d) => d instanceof Date);
  //       }

  //       // ----------- 2) DEFINE AXIS -----------------
  //       const xAxis = d3
  //         .axisBottom(x)
  //         .tickValues(tickValues)
  //         .tickFormat((d: any) => {
  //           const entry = currentData.find(cd => cd.start.getTime() === d.getTime());
  //           return entry ? entry.label : '';
  //         });

  //       const xAxisGroup = svg
  //         .append('g')
  //         .attr('class', 'x-axis')
  //         .attr('transform', `translate(0,${height - marginBottom + 1})`)
  //         .call(xAxis);

  //       xAxisGroup.selectAll('path, line').attr('stroke', colors.axis);
  //       xAxisGroup
  //         .selectAll('text')
  //         .attr('fill', colors.axisTick)
  //         .style('font-size', '12px')
  //         .style('font-family', 'Inter, sans-serif')
  //         .each(function (d, i) {
  //           if (i === 0) d3.select(this).attr('text-anchor', 'start');
  //           else d3.select(this).attr('text-anchor', 'end');
  //         });

  //       if (this.setBg) {
  //         const height = 30;
  //         const radius = 10;

  //         const path = `M0,0 H${width} V${height - radius} Q${width},${height} ${width - radius
  //           },${height} H${radius} Q0,${height} 0,${height - radius}Z`;

  //         xAxisGroup
  //           .insert('path', ':first-child')
  //           .attr('d', path)
  //           .attr('fill', colors.xAxisOverlayBg)
  //           .attr('stroke', ''); // optional stroke
  //       }
  //     }

  //     if (this.showYAxis) {
  //       let yAxis = d3.axisLeft(y);
  //       if (this.yAxisShowOnlyFirstLast) {
  //         const yTicks = y.ticks();
  //         if (this.showAllYticks) {
  //           yAxis.tickValues(yTicks);
  //         } else {
  //           yAxis.tickValues([yTicks[0], yTicks[yTicks.length - 1]]);
  //         }
  //       }
  //       const yAxisGroup = svg
  //         .append('g')
  //         .attr('transform', `translate(${marginLeft},0)`)
  //         .call(yAxis);
  //       if (this.showYAxisPath) {
  //         yAxisGroup.selectAll('path, line').attr('stroke', colors.axis);
  //       } else {
  //         yAxisGroup.selectAll('path, line').remove();
  //       }
  //       yAxisGroup
  //         .selectAll('text')
  //         .attr('fill', colors.axisTick)
  //         .style('font-size', '12px')
  //          .style('font-family', 'Inter, sans-serif');
  //     }

  //     if (this.showYAxisGrid) {
  //       svg
  //         .append('g')
  //         .attr('class', 'grid')
  //         .attr('transform', `translate(${marginLeft},0)`)
  //         .call(
  //           d3
  //             .axisLeft(y)
  //             .tickSize(-(width - marginLeft - marginRight))
  //             .tickFormat(() => '')
  //         )
  //         .selectAll('line')
  //         .attr('stroke', colors.gridLine)
  //         .attr('stroke-opacity', 1)
  //         .attr('stroke-dasharray', '2,2');
  //       svg.select('.grid .domain').remove();
  //     }

  //     if (this.showXAxisGrid) {

  //       // get highest Y value
  //       const maxVal = d3.max(currentData.map(d => d[this.valueKey])) || 0;

  //       // height between top Y and bottom X axis
  //       // const gridHeight = y(0) - y(maxVal);

  //       const gridHeight = height - marginTop - marginBottom;
  //       const xAxisY = y.range()[0];

  //       const xGrid = d3.axisBottom(x)
  //         .tickSize(-gridHeight)      // FIXED height
  //         .tickFormat(() => '');

  //       const xGridGroup = svg.append('g')
  //         .attr('class', 'x-grid')
  //         .attr('transform', `translate(0, ${xAxisY})`)   // align at x-axis
  //         .call(xGrid);

  //       xGridGroup.selectAll('line')
  //         .attr('stroke', colors.gridLine)
  //         .attr('stroke-opacity', 1)
  //         .attr('stroke-dasharray', '2,2');

  //       xGridGroup.select('.domain').remove();
  //     }

  //     const line = d3
  //       .line<any>()
  //       .x((d) => x(d.start))
  //       .y((d) => y(d.value))
  //       .defined((d) => typeof d.value === 'number')
  //       .curve(d3[this.curveType]);

  //     const drawLine = (data: any, strokeColor: string, shadow = false) => {
  //       if (shadow) {
  //         // Define a blur filter once (only if not already defined)
  //         if (svg.select('defs #line-shadow').empty()) {
  //           const defs = svg.append('defs');

  //           defs
  //             .append('filter')
  //             .attr('id', 'line-shadow')
  //             .append('feGaussianBlur')
  //             .attr('stdDeviation', 1.8); // controls blur amount
  //         }

  //         // Append blurred shadow path behind the main line
  //         svg
  //           .append('path')
  //           .datum(data)
  //           .attr('fill', 'none')
  //           .attr('stroke', strokeColor)
  //           .attr('stroke-width', 4) // thicker stroke for shadow
  //           .attr('d', line)
  //           .style('opacity', 0.2)
  //           .style('filter', 'url(#line-shadow)');
  //       }

  //       // Main sharp line on top
  //       svg
  //         .append('path')
  //         .datum(data)
  //         .attr('fill', 'none')
  //         .attr('stroke', strokeColor)
  //         .attr('stroke-width', 3)
  //         .attr('d', line);
  //     };

  //     let lines = [
  //       {
  //         data: [],
  //         storkColor: !this.isReverse
  //           ? colors.secondaryLine
  //           : colors.primaryLine,
  //       },
  //       {
  //         data: currentData,
  //         storkColor: this.isReverse
  //           ? colors.secondaryLine
  //           : colors.primaryLine,
  //       },
  //     ];

  //     if (previousData.length) {
  //       lines[0].data = previousData.map((p, index) => ({
  //         ...p,
  //         start: currentData[index]?.start || p.start,
  //       }));
  //     }

  //     if (this.isReverse) {
  //       lines = lines.reverse();
  //     }

  //     lines.map(({ data, storkColor }, index) =>
  //       drawLine(
  //         data,
  //         storkColor,
  //         (this.isReverse && index == 1) || (!this.isReverse && index == 0)
  //           ? true
  //           : false
  //       )
  //     );

  //     // Show circle strokes for all data points
  //     if (this.showCircleStrokes) {
  //       // Draw circles for current data
  //       const currentLineColor = this.isReverse ? colors.secondaryLine : colors.primaryLine;
  //       svg
  //         .selectAll('circle.current')
  //         .data(currentData.filter((d) => typeof d.value === 'number'))
  //         .join('circle')
  //         .attr('class', 'current')
  //         .attr('cx', (d) => x(d.start))
  //         .attr('cy', (d) => y(d.value))
  //         .attr('r', 4)
  //         .attr('fill', currentLineColor)
  //         .attr('stroke', currentLineColor)
  //         .attr('stroke-width', 2);

  //       // Draw circles for previous data if it exists
  //       if (previousData.length > 0) {
  //         const previousLineColor = !this.isReverse ? colors.secondaryLine : colors.primaryLine;
  //         svg
  //           .selectAll('circle.previous')
  //           .data(previousData.filter((d) => typeof d.value === 'number'))
  //           .join('circle')
  //           .attr('class', 'previous')
  //           .attr('cx', (d, i) => x(currentData[i]?.start || d.start))
  //           .attr('cy', (d) => y(d.value))
  //           .attr('r', 4)
  //           .attr('fill', previousLineColor)
  //           .attr('stroke', previousLineColor)
  //           .attr('stroke-width', 2);
  //       }
  //     }
  //     if (this.showCircleStrokesWithStroke) {
  //       // Draw circles for current data
  //       // Draw circles for current data
  //       const lineColor = this.isReverse ? colors.secondaryLine : colors.primaryLine;

  //       svg.selectAll('circle.current')
  //         .data(currentData.filter(d => typeof d.value === 'number'))
  //         .join('circle')
  //         .attr('class', 'current')
  //         .attr('cx', d => x(d.start))
  //         .attr('cy', d => y(d.value))
  //         .attr('r', 6)                 // outer radius
  //         .attr('fill', lineColor)      // white center
  //         .attr('stroke', '#FFFFFF')    // colored outer ring
  //         .attr('stroke-width', 3);     // thickness (adjust as needed)

  //       // Draw circles for previous data if it exists
  //       if (previousData.length > 0) {
  //         const prevColor = !this.isReverse ? colors.secondaryLine : colors.primaryLine;

  //         svg.selectAll('circle.previous')
  //           .data(previousData.filter(d => typeof d.value === 'number'))
  //           .join('circle')
  //           .attr('class', 'previous')
  //           .attr('cx', (d, i) => x(currentData[i]?.start || d.start))
  //           .attr('cy', d => y(d.value))
  //           .attr('r', 6)
  //           .attr('fill', prevColor)
  //           .attr('stroke', '#ffffff')
  //           .attr('stroke-width', 3);

  //       }
  //     }

  //     const t = this.getTooltipStyleByTheme(colors);

  //     // const tooltip = d3
  //     //   .select(container)
  //     //   .append('div')
  //     //   .attr('class', 'tooltip')
  //     //   .style('opacity', 0)
  //     //   .style('position', 'absolute')
  //     //   .style('pointer-events', 'none')
  //     //   .style('padding', '0.5rem');
  //     const tooltip = d3
  //       .select(container)
  //       .append('div')
  //       .attr('class', 'tooltip')
  //       .style('opacity', 0)
  //       .style('position', 'absolute')
  //       .style('pointer-events', 'none')
  //       .style('background', t.background)
  //       .style('border', t.border)
  //       .style('border-radius', '20px')
  //       .style('padding', '16px 20px')
  //       .style('box-shadow', t.shadow)
  //       .style('color', t.text)
  //       .style('font-family', 'Inter, sans-serif')
  //       .style('white-space', 'nowrap')
  //       .style('transition', 'opacity 0.15s ease');

  //     const hoverGroup = svg
  //       .append('g')
  //       .attr('class', 'hover-group')
  //       .style('display', 'none');
  //     hoverGroup
  //       .append('line')
  //       .attr('class', 'hover-line')
  //       .attr('y1', marginTop)
  //       .attr('y2', height - marginBottom)
  //       .attr('stroke', colors.hoverLine)
  //       .attr('stroke-width', 1)
  //       .attr('stroke-dasharray', '2,2');
  //     hoverGroup
  //       .append('text')
  //       .attr('class', 'hover-label')
  //       .attr('y', height - marginBottom + 20)
  //       .attr('text-anchor', 'middle')
  //       .attr('fill', colors.axisLabel)
  //       .style('font-size', '12px');

  //     svg
  //       .append('rect')
  //       .attr('class', 'overlay')
  //       .attr('x', marginLeft)
  //       .attr('y', marginTop)
  //       .attr('width', width - marginLeft - marginRight)
  //       .attr('height', height - marginTop - marginBottom)
  //       .attr('fill', 'transparent')
  //       .on('mouseover', () => {
  //         hoverGroup.style('display', null);
  //         tooltip.style('opacity', 1);
  //         svg.selectAll('.x-axis .tick text').style('opacity', 0);
  //       })
  //       .on('mouseout', () => {
  //         hoverGroup.style('display', 'none');
  //         tooltip.style('opacity', 0);
  //         svg.selectAll('.x-axis .tick text').style('opacity', 1);
  //       })
  //       .on('mousemove', (event: MouseEvent) => {
  //         const [mx] = d3.pointer(event);
  //         const hoveredDate = x.invert(mx);
  //         const bisector = d3.bisector((d: any) => d.start).left;
  //         const index = bisector(currentData, hoveredDate, 1);
  //         const d0 = currentData[index - 1];
  //         const d1 = currentData[index];
  //         const d = !d0
  //           ? d1
  //           : !d1
  //             ? d0
  //             : hoveredDate.getTime() - d0.start.getTime() >
  //               d1.start.getTime() - hoveredDate.getTime()
  //               ? d1
  //               : d0;
  //         if (!d) return;

  //         const dataX = x(d.start);
  //         hoverGroup.select('.hover-line').attr('x1', dataX).attr('x2', dataX);
  //         const hoverLabel = hoverGroup.select('.hover-label').text(d.label);
  //         const labelWidth = (hoverLabel.node() as SVGTextElement).getBBox()
  //           .width;
  //         let labelX = dataX;
  //         if (labelX - labelWidth / 2 < marginLeft)
  //           labelX = marginLeft + labelWidth / 2;
  //         else if (labelX + labelWidth / 2 > width - marginRight)
  //           labelX = width - marginRight - labelWidth / 2;
  //         hoverLabel.attr('x', labelX);

  //         const prevPoint = previousData[currentData.indexOf(d)];
  //         let prevValue =
  //           !this.isReverse && prevPoint ? prevPoint.value : d.value;
  //         let currValue = this.isReverse && prevPoint ? prevPoint.value : d.value;
  //         const percentChange = prevPoint
  //           ? this.calculatePercentChange(currValue, prevValue)
  //           : 0;
  //         // tooltip.html(
  //         //   this.customToolTipHtml(
  //         //     !!prevPoint,
  //         //     percentChange,
  //         //     d.label,
  //         //     prevPoint?.label,
  //         //     d.value,
  //         //     prevPoint?.value,
  //         //     this.isAmount,
  //         //     d.tooltipExtraData || []
  //         //   )
  //         // );

  // //         tooltip.html(`
  // //   <div style="
  // //     display:flex;
  // //     flex-direction:column;
  // //     gap:4px;
  // //   ">

  // //     <div style="
  // //       font-size:16px;
  // //       font-weight:600;
  // //     ">${d.label}</div>

  // //     <div style="
  // //       display:flex;
  // //       align-items:center;
  // //       gap:8px;
  // //       font-size:14px;
  // //       color:${t.subtitleColor};
  // //     ">
  // //       <span>Active Businesses: <b>${d.value}</b></span>
  // //     </div>

  // //   </div>
  // // `);

  // // Build tooltip rows for the hovered X position
  // const seriesRows: { name: string; color: string; value: number }[] = [];

  // // Determine colors used for each series (same logic as for lines / circles)
  // const currentColor = this.isReverse ? colors.secondaryLine : colors.primaryLine;
  // const previousColor = !this.isReverse ? colors.secondaryLine : colors.primaryLine;

  // // 1) Current series point (always exists)
  // seriesRows.push({
  //   name: d.name || 'Series 1',
  //   color: currentColor,
  //   value: d.value
  // });

  // // 2) Other series point at same index, if available
  // if (previousData.length > 0) {
  //   const prevPoint = previousData[currentData.indexOf(d)];
  //   if (prevPoint) {
  //     seriesRows.push({
  //       name: prevPoint.name || 'Series 2',
  //       color: previousColor,
  //       value: prevPoint.value
  //     });
  //   }
  // }

  // // Build HTML
  // let tooltipHTML = `
  //   <div style="display:flex;flex-direction:column;gap:6px;">
  //     <div style="font-size:16px;font-weight:600;">${d.label}</div>
  // `;

  // // Add one row per series (1 or 2)
  // seriesRows.forEach(row => {
  //   tooltipHTML += `
  //     <div style="display:flex;justify-content:space-between;font-size:14px; gap:8px;">
  //       <span style="color:${row.color};font-weight:600;">${row.name}</span>
  //       <span style="font-weight:600;color:${t.text};">${row.value}</span>
  //     </div>
  //   `;
  // });

  // tooltipHTML += `</div>`;
  // tooltip.html(tooltipHTML);

  //         const [ptrX, ptrY] = d3.pointer(event, container);
  //         const tooltipNode = tooltip.node() as HTMLElement;
  //         const tooltipWidth = tooltipNode.getBoundingClientRect().width;
  //         const tooltipHeight = tooltipNode.getBoundingClientRect().height;

  //         const horizontalOffset = 15;
  //         const verticalOffset = 5;

  //         let tooltipLeft = ptrX + horizontalOffset;
  //         if (tooltipLeft + tooltipWidth > width) {
  //           tooltipLeft = ptrX - horizontalOffset - tooltipWidth;
  //         }

  //         let tooltipTop = ptrY - verticalOffset - tooltipHeight;

  //         if (tooltipTop < 0) {
  //           tooltipTop = 0;
  //         }

  //         tooltip
  //           .style('left', `${tooltipLeft}px`)
  //           .style('top', `${tooltipTop}px`);
  //       });
  //   }

  private createChart(): void {
    const container = this.chartContainer.nativeElement;
    if (!container) return;

    const width = container.clientWidth;
    if (width <= 0) return;

    // if there's no data at all, clear and exit
    const rawInputs = [
      this.data1,
      this.data2,
      this.data3,
      this.data4,
      this.data5,
      this.data6,
      this.data7,
    ];
    const nonEmptySets = rawInputs.filter(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
    if (!nonEmptySets.length) {
      d3.select(container).selectAll('*').remove();
      while (container.firstChild) container.removeChild(container.firstChild);
      return;
    }

    // clean container
    d3.select(container).selectAll('*').remove();
    while (container.firstChild) container.removeChild(container.firstChild);

    const colors = this.getThemeColors();
    const formatter = this.unitFormatter();

    // Convert incoming raw datasets into normalized series (Date objects, label, value, name, tooltipExtraData)
    const seriesSets = nonEmptySets.map((dataset) =>
      dataset
        .map((ds) => {
          const tooltipExtra = this.tooltipExtraData?.length
            ? this.tooltipExtraData
                .filter((extra) =>
                  Object.prototype.hasOwnProperty.call(ds, extra.key)
                )
                .map((extra) => ({
                  label: extra.label,
                  value: ds[extra.key],
                  isAmount: extra.isAmount,
                }))
            : [];

          return {
            raw: ds,
            start: this.timezoneDate(ds?.start),
            end: this.timezoneDate(ds?.end),
            value:
              typeof ds[this.valueKey] === 'number'
                ? ds[this.valueKey]
                : Number(ds[this.valueKey]) || 0,
            label: formatter(
              this.timezoneDate(ds.start) || new Date(),
              this.timezoneDate(ds.end)
            ),
            tooltipExtraData: tooltipExtra,
            name: ds.key || '',
          };
        })
        .sort((a, b) => {
          const aTime = a.start?.getTime() ?? 0;
          const bTime = b.start?.getTime() ?? 0;
          return aTime - bTime;
        })
    );

    // Primary series for X-axis alignment (first non-empty dataset)
    const primary = seriesSets[0];

    // Align other series to primary indices: ensure same length by mapping to primary indexes
    const alignedSeries = seriesSets.map((set, idx) => {
      if (idx === 0) return set;
      // map each element of primary: try to pick element at same index if exists, otherwise fallback to last known
      return primary.map((p, i) => {
        const s = set[i];
        if (s && s.start) {
          return { ...s, start: p.start }; // align time to primary
        }
        // if missing, create placeholder (keeps the index alignment)
        return {
          raw: null,
          start: p.start,
          end: p.end ?? p.start,
          value: NaN,
          label: p.label,
          tooltipExtraData: [],
          name: set[0]?.name || `Series ${idx + 1}`,
        };
      });
    });

    // apply yearIndexModify against primary to handle leap insertion holes if applicable
    // yearIndexModify expects (currentData, previousData) - we'll use previousData as empty array.
    const yi = this.yearIndexModify(alignedSeries[0], []);
    const primaryAdjusted = yi.currentData;

    // If yearIndexModify injected a placeholder, replicate that insertion into all series so indices remain aligned
    if (yi.currentData !== alignedSeries[0]) {
      // find differences in length
      const delta = primaryAdjusted.length - alignedSeries[0].length;
      if (delta > 0) {
        // We assume yearIndexModify inserted exactly one placeholder at an index; find index by comparing starts
        const insertedIndex = primaryAdjusted.findIndex((d, i) => {
          const orig = alignedSeries[0][i];
          // placeholder likely has 'no data' or non-number; compare label or typeof value
          return !orig || !orig.start || orig.start.getTime() !== d.start?.getTime();
        });
        if (insertedIndex >= 0) {
          // insert a placeholder into each aligned series at insertedIndex
          alignedSeries.forEach((s, si) => {
            const sample = s[insertedIndex - 1] ?? s[0];
            const dummy = sample
              ? { ...sample, label: '-', value: 'no data' as any, raw: null }
              : {
                  start: primaryAdjusted[insertedIndex].start,
                  end: primaryAdjusted[insertedIndex].end,
                  value: 'no data' as any,
                  label: '-',
                  tooltipExtraData: [],
                  name: s[0]?.name || `Series ${si + 1}`,
                  raw: null,
                };
            s.splice(insertedIndex, 0, dummy);
          });
        }
      }
    }

    // Now final aligned series to draw = alignedSeries (all have same length as primaryAdjusted)
    const finalPrimary = alignedSeries[0];

    // compute global numeric domain across all series values
    const allValues = alignedSeries
      .flatMap((s) =>
        s.map((d) => (typeof d.value === 'number' ? d.value : NaN))
      )
      .filter((v) => !isNaN(v));
    const maxValue = allValues.length ? (d3.max(allValues) as number) : 0;
    const yTempScale = d3.scaleLinear().domain([0, maxValue]).nice();
    const yLabels = yTempScale.ticks().map((t) => t.toString());
    const widestLabelWidth = this.getWidestLabelWidth(yLabels);

    const height = container.clientHeight || this.minHeight;

    if (width <= 0) return;
    const marginLeft =
      this.marginProps.left ?? (this.showYAxis ? widestLabelWidth + 15 : 10);
    const marginRight = this.marginProps.right ?? 20;
    const marginTop = this.marginProps.top ?? this.marginTop;
    const marginBottom = this.marginProps.bottom ?? (this.showXAxis ? this.marginBottom : 10);

    const minDate = d3.min(finalPrimary, (d) => d.start)!;
    const maxDate = d3.max(finalPrimary, (d) => d.start)!;

    const x = d3
      .scaleUtc()
      .domain([minDate, maxDate])
      .range([marginLeft, Math.max(marginLeft, width - marginRight)]);
    const y = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([Math.max(marginTop, height - marginBottom), marginTop])
      .nice();

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('overflow', 'visible');
    if (this.setBg) svg.style('background', colors.backgroundGradient);



    // X axis
    if (this.showXAxis) {
      let tickValues: any[];
      if (this.xAxisShowOnlyFirstLast) {
        const firstDate = finalPrimary[0]?.start;
        const lastDate = finalPrimary[finalPrimary.length - 1]?.start;
        tickValues = [firstDate, lastDate].filter(Boolean);
      } else if (this.showAllXLabels) {
        tickValues = finalPrimary.map((d) => d.start);
      } else {
        // Default behavior: show only first and last if not all labels are requested
        const firstDate = finalPrimary[0]?.start;
        const lastDate = finalPrimary[finalPrimary.length - 1]?.start;
        tickValues = [firstDate, lastDate].filter(Boolean);
      }

      const xAxis = d3
        .axisBottom(x)
        .tickValues(tickValues)
        .tickFormat((d: any) => {
          const entry = finalPrimary.find(
            (cd) => cd.start?.getTime() === (d as Date).getTime()
          );
          return entry ? entry.label : '';
        });

      const xAxisGroup = svg
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${height - marginBottom + 1})`)
        .call(xAxis);
      xAxisGroup.selectAll('path, line').attr('stroke', colors.axis);
      xAxisGroup
        .selectAll('text')
        .attr('fill', colors.axisTick)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif')
        .each(function (d, i) {
          if (i === 0) d3.select(this).attr('text-anchor', 'start');
          else d3.select(this).attr('text-anchor', 'end');
        });

      if (this.setBg) {
        const topH = 30;
        const radius = 10;
        const path = `M0,0 H${width} V${topH - radius} Q${width},${topH} ${
          width - radius
        },${topH} H${radius} Q0,${topH} 0,${topH - radius}Z`;
        xAxisGroup
          .insert('path', ':first-child')
          .attr('d', path)
          .attr('fill', colors.xAxisOverlayBg)
          .attr('stroke', '');
      }
    }

    // Additional Y axis display if requested
    if (this.showYAxis) {
      let yAxis = d3.axisLeft(y);
      if (this.yAxisShowOnlyFirstLast) {
        const yTicks = y.ticks();
        if (this.showAllYticks) yAxis.tickValues(yTicks);
        else yAxis.tickValues([yTicks[0], yTicks[yTicks.length - 1]]);
      }
      const yAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(yAxis);
      if (this.showYAxisPath)
        yAxisGroup.selectAll('path, line').attr('stroke', colors.axis);
      else yAxisGroup.selectAll('path, line').remove();
      yAxisGroup
        .selectAll('text')
        .attr('fill', colors.axisTick)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');
    }

    if (this.showYAxisGrid) {
      const gridGroup = svg
        .append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(
          d3
            .axisLeft(y)
            .tickSize(-(width - marginLeft - marginRight))
            .tickSizeOuter(0)
            .tickFormat(() => '')
        );
      gridGroup
        .selectAll('line')
        .attr('stroke', colors.axis) // Set base color for all lines (including baseline)
        .filter((d: any) => d !== 0) // Dash only non-baseline lines
        .attr('stroke-opacity', 0.6)
        .attr('stroke-dasharray', '2,2');
      gridGroup.select('.domain').remove();
    }

    if (this.showXAxisGrid) {
      const gridHeight = height - marginTop - marginBottom;
      const xAxisY = y.range()[0];
      const xGrid = d3
        .axisBottom(x)
        .tickSize(-gridHeight)
        .tickSizeOuter(0)
        .tickFormat(() => '');
      const xGridGroup = svg
        .append('g')
        .attr('class', 'x-grid')
        .attr('transform', `translate(0, ${xAxisY})`)
        .call(xGrid);
      xGridGroup
        .selectAll('line')
        .attr('stroke', colors.gridLine)
        .attr('stroke-opacity', 0.5)
        .attr('stroke-dasharray', '2,2');
      xGridGroup.select('.domain').remove();
    }

    // line generator
    const lineGen = d3
      .line<any>()
      .x((d) => x(d.start))
      .y((d) => y(d.value))
      .defined((d) => typeof d.value === 'number')
      .curve(d3[this.curveType]);

    // palette: base on theme colors + extras
    const palette = [
      colors.primaryLine,
      colors.secondaryLine,
      colors.tertiaryLine,
      colors.quaternaryLine,
      colors.quinaryLine,
      colors.senaryLine,
      colors.septenaryLine,
      colors.octonaryLine,
      colors.nonaryLine,
      colors.denaryLine,
    ];

    // create structured lines array
    const lines = alignedSeries.map((dataset, idx) => ({
      data: dataset,
      color: palette[idx % palette.length],
      name: dataset[0]?.name || `Series ${idx + 1}`,
    }));

    // draw each line
    lines.forEach((lineObj, i) => {
      // shadow optionally for first two series to mimic previous behavior: keep simple - no shadow here
      // draw path
      svg
        .append('path')
        .datum(lineObj.data)
        .attr('fill', 'none')
        .attr('stroke', lineObj.color)
        .attr('stroke-width', 3)
        .attr('stroke-dasharray', this.dashedSeries.includes(i) ? '5,5' : '1,0')
        .attr('d', lineGen as any);
    });

    // draw circles per series (normal and outlined if requested)
    lines.forEach((lineObj, lineIndex) => {
      if (this.showCircleStrokes) {
        svg
          .selectAll(`circle.series-${lineIndex}`)
          .data(lineObj.data.filter((d) => typeof d.value === 'number'))
          .join('circle')
          .attr('class', `series-${lineIndex}`)
          .attr('cx', (d) => x(d.start!))
          .attr('cy', (d) => y(d.value))
          .attr('r', 4)
          .attr('fill', lineObj.color)
          .attr('stroke', lineObj.color)
          .attr('stroke-width', 2);
      }
      if (this.showCircleStrokesWithStroke && !this.strokeWithLineColor) {
        svg
          .selectAll(`circle.outlined-${lineIndex}`)
          .data(lineObj.data.filter((d) => typeof d.value === 'number'))
          .join('circle')
          .attr('class', `outlined-${lineIndex}`)
          .attr('cx', (d) => x(d.start!))
          .attr('cy', (d) => y(d.value))
          .attr('r', 6)
          .attr('fill', lineObj.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 2);
      }

      if (this.strokeWithLineColor && !this.showCircleStrokesWithStroke) {
        let strokeFillColor = '';
        if (this.strokeWithLineColor) {
          const isDarkTheme = this.currentTheme === 'dark';
          strokeFillColor = isDarkTheme ? '#151517' : '#fff';
        } else {
          strokeFillColor = '#fff';
        }

        svg
          .selectAll(`circle.outlined-${lineIndex}`)
          .data(lineObj.data.filter((d) => typeof d.value === 'number'))
          .join('circle')
          .attr('class', `outlined-${lineIndex}`)
          .attr('cx', (d) => x(d.start!))
          .attr('cy', (d) => y(d.value))
          .attr('r', 6)
          .attr('fill', strokeFillColor)
          .attr('stroke', this.strokeWithLineColor ? lineObj.color : '#fff')
          .attr('stroke-width', 2);
      }
    });

    // tooltip setup
    const t = this.getTooltipStyleByTheme(colors);
    const tooltip = d3
      .select(container)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', t.background)
      .style('border', t.border)
      .style('border-radius', '10px')
      .style('padding', '12px 16px')
      .style('box-shadow', t.shadow)
      .style('color', t.text)
      .style('font-family', 'Inter, sans-serif')
      .style('white-space', 'nowrap')
      .style('transition', 'opacity 0.15s ease');

    const hoverGroup = svg
      .append('g')
      .attr('class', 'hover-group')
      .style('display', 'none');
    hoverGroup
      .append('line')
      .attr('class', 'hover-line')
      .attr('y1', marginTop)
      .attr('y2', height - marginBottom)
      .attr('stroke', colors.hoverLine)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2');
    hoverGroup
      .append('text')
      .attr('class', 'hover-label')
      .attr('y', height - marginBottom + 20)
      .attr('text-anchor', 'middle')
      .attr('fill', colors.axisLabel)
      .style('font-size', '12px');

    svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('x', marginLeft)
      .attr('y', marginTop)
      .attr('width', Math.max(0, width - marginLeft - marginRight))
      .attr('height', Math.max(0, height - marginTop - marginBottom))
      .attr('fill', 'transparent')
      .on('mouseover', () => {
        hoverGroup.style('display', null);
        tooltip.style('opacity', 1);
        svg.selectAll('.x-axis .tick text').style('opacity', 0);
      })
      .on('mouseout', () => {
        hoverGroup.style('display', 'none');
        tooltip.style('opacity', 0);
        svg.selectAll('.x-axis .tick text').style('opacity', 1);
      })
      .on('mousemove', (event: MouseEvent) => {
        const [mx, my] = d3.pointer(event);
        const hoveredDate = x.invert(mx);
        // use bisector on primary series
        const bisector = d3.bisector((d: any) => d.start).left;
        const idx = bisector(finalPrimary, hoveredDate, 1);
        const d0 = finalPrimary[idx - 1];
        const d1 = finalPrimary[idx];
        const d = !d0
          ? d1
          : !d1
          ? d0
          : (hoveredDate.getTime() - (d0.start?.getTime() ?? 0)) >
            ((d1.start?.getTime() ?? 0) - hoveredDate.getTime())
          ? d1
          : d0;
        if (!d || !d.start) return;

        const dataX = x(d.start);
        hoverGroup.select('.hover-line').attr('x1', dataX).attr('x2', dataX);
        const hoverLabel = hoverGroup.select('.hover-label').text(d.label);
        const labelWidth = (hoverLabel.node() as SVGTextElement).getBBox()
          .width;
        let labelX = dataX;
        if (labelX - labelWidth / 2 < marginLeft)
          labelX = marginLeft + labelWidth / 2;
        else if (labelX + labelWidth / 2 > width - marginRight)
          labelX = width - marginRight - labelWidth / 2;
        hoverLabel.attr('x', labelX);

        // Build tooltip rows for every series at this index
        const seriesRows: { name: string; color: string; value: any }[] = [];
        lines.forEach((lineObj, sIndex) => {
          // find corresponding point in this series aligned to primary index
          const point =
            alignedSeries[sIndex][finalPrimary.indexOf(d)] ||
            alignedSeries[sIndex].find(
              (pt) => pt.start?.getTime() === d.start?.getTime()
            );
          const val = point
            ? point.value
            : lineObj.data[finalPrimary.indexOf(d)]?.value ?? '-';
          seriesRows.push({
            name: lineObj.name || `Series ${sIndex + 1}`,
            color: lineObj.color,
            value:
              typeof val === 'number'
                ? val
                : isNaN(Number(val))
                ? val
                : Number(val),
          });
        });

        // Build tooltip HTML
        const fmtVal = (val: any): string => {
          if (val == null || (typeof val === 'number' && isNaN(val))) return '-';
          if (typeof val !== 'number') return String(val);
          switch (this.valueFormat) {
            case 'currency': return `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
            case 'percent': return `${val.toFixed(1)}%`;
            default: return val.toLocaleString('en-US');
          }
        };
        let tooltipHTML = `<div style="display:flex;flex-direction:column;gap:8px;">`;
        tooltipHTML += `<div style="font-size:15px;font-weight:600;color:${t.text};margin-bottom:4px;">${d.label}</div>`;
        seriesRows.forEach((row) => {
          tooltipHTML += `
          <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;font-size:13px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="color:${row.color};font-weight:500;">${
            row.name
          }</span>
            </div>
            <div style="font-weight:700;color:${row.color};">${
            fmtVal(row.value)
          }</div>
          </div>
        `;
        });
        tooltipHTML += `</div>`;
        tooltip.html(tooltipHTML);

        // position tooltip near pointer
        const tooltipNode = tooltip.node() as HTMLElement;
        const tooltipRect = tooltipNode.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        // pointer coords relative to container
        const ptrX = mx + containerRect.left;
        const ptrY = my + containerRect.top;

        let left = ptrX + 15;
        if (left + tooltipRect.width > containerRect.left + width)
          left = ptrX - 15 - tooltipRect.width;
        let top = ptrY - 10 - tooltipRect.height;
        if (top < containerRect.top) top = containerRect.top;

        tooltip
          .style('left', `${left - containerRect.left}px`)
          .style('top', `${top - containerRect.top}px`);
      });
  }

  getTooltipStyleByTheme(colors: any) {
    const isDark = this.currentTheme === 'dark';

    return {
      background: isDark ? '#232327' : '#F5F5F5',
      text: isDark ? '#ffffff' : '#141437',
      shadow: isDark
        ? '0px 4px 20px rgba(0,0,0,0.35)'
        : '0px 4px 20px rgba(0,0,0,0.15)',
      border: isDark ? '1px solid #2A2A2E' : '1px solid #EAEAEA',
      subtitleColor: '#AD46FF',
    };
  }
}
